import React, { createContext, useEffect, useState, useMemo } from "react";
import { LayerTypeProviderProps } from "../../../types";
import { noop } from "lodash";
import { useSnapshot } from "valtio";
import { isZoopla } from "react-migration/domains/comparables/util/isZoopla";
import { comparablesStore } from "src/js/stores/comparables";
import { ZooplaComparableDTO } from "react-migration/lib/typings/Zoopla";
import { RecordType } from "react-migration/domains/comparables/typings/Record";
import { EnrichedComparableDTO } from "react-migration/lib/typings/EnrichedComparables";
import {
  ComparableDistributionHookResponse,
  useComparableDistributionQuery,
} from "react-migration/domains/comparables/hooks/useComparableDistributionQuery";
import { disabledEPCFields, disabledZooplaFields } from "react-migration/domains/comparables/util/DisabledFilters";
import { syncStoreToLocalStorage } from "src/js/stores/comparables/effects";

export interface ComparablesContextType {
  transactions: RecordType[];
  setTransactions: React.Dispatch<React.SetStateAction<EnrichedComparableDTO[]>>;
  zooplaTransactions: ZooplaComparableDTO[];
  setZooplaTransactions: React.Dispatch<React.SetStateAction<ZooplaComparableDTO[]>>;
  distribution: ComparableDistributionHookResponse;
}

export const ComparablesLayerContext = createContext<ComparablesContextType>({
  transactions: [],
  setTransactions: noop,
  zooplaTransactions: [],
  setZooplaTransactions: noop,
  distribution: {
    loading: false,
    error: undefined,
    data: undefined,
  },
});

export function ComparablesLayerContextProvider({ children }: LayerTypeProviderProps) {
  const [transactions, setTransactions] = useState<EnrichedComparableDTO[]>([]);
  const [zooplaTransactions, setZooplaTransactions] = useState<ZooplaComparableDTO[]>([]);
  const { dataSource, hmlrFilters, hometrackFilters, priceMode } = useSnapshot(comparablesStore);
  // TODO: Properly split out filter handling instead of selecting based on data source
  // Select appropriate filters based on data source
  const currentFilters = dataSource === "hmlr" ? hmlrFilters : hometrackFilters;

  const disabledFields = isZoopla(priceMode) ? disabledZooplaFields : disabledEPCFields;
  const distribution = useComparableDistributionQuery({
    filters: currentFilters,
    disabledFields,
    field: priceMode,
  });
  useEffect(() => syncStoreToLocalStorage(), []);

  const contextValue = useMemo(
    () => ({
      transactions,
      setTransactions,
      zooplaTransactions,
      setZooplaTransactions,
      distribution,
    }),
    [transactions, setTransactions, zooplaTransactions, setZooplaTransactions, distribution]
  );

  return <ComparablesLayerContext.Provider value={contextValue}>{children}</ComparablesLayerContext.Provider>;
}
